import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Linkedin, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import davidThomasPortrait from "@/assets/david-thomas-portrait.jpg";
import peterDinglePortrait from "@/assets/peter-dingle-capital.jpg";
import FocalPointPortrait from "@/components/FocalPointPortrait";
import PageSeo from "@/components/PageSeo";
import { useLanguage } from "@/i18n/LanguageContext";


const BOOKING_URL = "https://calendar.app.google/smFTnDe7izrtAPVg6";

const CapitalRaising = () => {
  const { t } = useLanguage();

  const process = [
    { title: t("capital.process.step1.title"), body: t("capital.process.step1.body") },
    { title: t("capital.process.step2.title"), body: t("capital.process.step2.body") },
    { title: t("capital.process.step3.title"), body: t("capital.process.step3.body") },
    { title: t("capital.process.step4.title"), body: t("capital.process.step4.body") },
  ];

  const regions = [
    t("capital.advantage.region1"),
    t("capital.advantage.region2"),
    t("capital.advantage.region3"),
    t("capital.advantage.region4"),
    t("capital.advantage.region5"),
  ];

  const discipline = [
    t("capital.discipline.item1"),
    t("capital.discipline.item2"),
    t("capital.discipline.item3"),
  ];

  const commercial = [
    t("capital.commercial.item1"),
    t("capital.commercial.item2"),
    t("capital.commercial.item3"),
  ];

  const expectations = [
    t("capital.expectation.item1"),
    t("capital.expectation.item2"),
    t("capital.expectation.item3"),
    t("capital.expectation.item4"),
  ];

  return (
    <main className="min-h-screen bg-background">
      <PageSeo
        title={t("seo.capital.title")}
        description={t("seo.capital.description")}
        path="/capital-raising"
      />
      <Navigation />



      {/* Hero */}
      <section className="pt-40 pb-28 bg-gradient-to-br from-primary via-foreground/95 to-foreground/90">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center text-white space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent-mint">
              {t("capital.hero.eyebrow")}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              {t("capital.hero.title")}
            </h1>
            <p className="text-lg sm:text-xl text-white/85 font-serif leading-relaxed max-w-2xl mx-auto">
              {t("capital.hero.body")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-2 text-sm text-white/70 font-sans">
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-mint" />
                {t("capital.hero.tag1")}
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-mint" />
                {t("capital.hero.tag2")}
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-mint" />
                {t("capital.hero.tag3")}
              </span>
            </div>
            <p className="text-sm uppercase tracking-[0.2em] text-accent-mint/90 font-sans pt-4">
              {t("capital.hero.byline")}
            </p>
          </div>
        </div>
      </section>

      {/* Who this is for + Why us — sharpened entry block */}
      <section className="py-24 border-b border-border/50 bg-secondary/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 lg:gap-16">
            <div className="space-y-5">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                {t("capital.who.eyebrow")}
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                {t("capital.who.title")}
              </h2>
              <ul className="space-y-3 text-base text-muted-foreground font-serif leading-relaxed">
                <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.who.item1")}</span></li>
                <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.who.item2")}</span></li>
                <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.who.item3")}</span></li>
                <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.who.item4")}</span></li>
              </ul>
            </div>
            <div className="space-y-5">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                {t("capital.why.eyebrow")}
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                {t("capital.why.title")}
              </h2>
              <ul className="space-y-3 text-base text-muted-foreground font-serif leading-relaxed">
                <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span><span className="text-foreground font-medium">{t("capital.why.item1.name")}</span>{t("capital.why.item1.rest")}</span></li>
                <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span><span className="text-foreground font-medium">{t("capital.why.item2.name")}</span>{t("capital.why.item2.rest")}</span></li>
                <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.why.item3")}</span></li>
                <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.why.item4")}</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Peter Dingle profile */}
      <section className="py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-14">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              {/* Portrait */}
              <div className="lg:col-span-5">
                <FocalPointPortrait
                  src={peterDinglePortrait}
                  alt={t("capital.peter.alt")}
                  initials="PD"
                />
                <div className="mt-6 space-y-3">
                  <p className="text-lg font-bold tracking-tight">{t("capital.peter.name")}</p>
                  <p className="text-sm text-muted-foreground font-serif">
                    {t("capital.peter.role")}
                  </p>
                  <div className="flex flex-wrap gap-3 pt-2">
                    <a
                      href="https://www.linkedin.com/in/petermdingle/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-border bg-background text-sm font-medium tracking-wide hover:border-accent hover:text-accent transition-colors font-sans"
                    >
                      <Linkedin className="h-4 w-4" />
                      {t("capital.peter.linkedin")}
                    </a>
                    <a
                      href="https://petermdingle.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-border bg-background text-sm font-medium tracking-wide hover:border-accent hover:text-accent transition-colors font-sans"
                    >
                      <Globe className="h-4 w-4" />
                      {t("capital.peter.website")}
                    </a>
                  </div>
                </div>
              </div>

              {/* Intro */}
              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                  {t("capital.peter.eyebrow")}
                </p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                  {t("capital.peter.title")}
                </h2>
                <p className="text-lg text-muted-foreground font-serif leading-relaxed">
                  {t("capital.peter.intro")}
                </p>
                <div className="space-y-5 text-base text-muted-foreground font-serif leading-relaxed border-l-2 border-accent pl-6">
                  <p>
                    {t("capital.peter.bio1")}
                  </p>
                  <p>
                    {t("capital.peter.bio2")}
                  </p>
                  <p>
                    {t("capital.peter.bio3")}
                  </p>
                  <p className="text-foreground font-medium">
                    {t("capital.peter.bio4")}
                  </p>
                </div>
              </div>
            </div>

            {/* Credential stats */}
            <div className="grid md:grid-cols-3 gap-px bg-border/60 border border-border/60">
              <div className="bg-background p-8 space-y-2">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.peter.stat1.label")}</p>
                <p className="text-lg font-bold tracking-tight">{t("capital.peter.stat1.value")}</p>
                <p className="text-sm text-muted-foreground font-serif">{t("capital.peter.stat1.detail")}</p>
              </div>
              <div className="bg-background p-8 space-y-2">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.peter.stat2.label")}</p>
                <p className="text-lg font-bold tracking-tight">{t("capital.peter.stat2.value")}</p>
                <p className="text-sm text-muted-foreground font-serif">{t("capital.peter.stat2.detail")}</p>
              </div>
              <div className="bg-background p-8 space-y-2">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.peter.stat3.label")}</p>
                <p className="text-lg font-bold tracking-tight">{t("capital.peter.stat3.value")}</p>
                <p className="text-sm text-muted-foreground font-serif">{t("capital.peter.stat3.detail")}</p>
              </div>
            </div>

            {/* Extended credentials */}
            <div className="grid md:grid-cols-2 gap-10 lg:gap-14">
              <div className="space-y-4">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.peter.credentials.label")}</p>
                <h3 className="text-2xl font-bold tracking-tight">{t("capital.peter.credentials.title")}</h3>
                <ul className="space-y-3 text-base text-muted-foreground font-serif leading-relaxed">
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.peter.credentials.item1")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.peter.credentials.item2")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.peter.credentials.item3")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.peter.credentials.item4")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.peter.credentials.item5")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.peter.credentials.item6")}</span></li>
                </ul>
              </div>
              <div className="space-y-4">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.peter.focus.label")}</p>
                <h3 className="text-2xl font-bold tracking-tight">{t("capital.peter.focus.title")}</h3>
                <ul className="space-y-3 text-base text-muted-foreground font-serif leading-relaxed">
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.peter.focus.item1")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.peter.focus.item2")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.peter.focus.item3")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.peter.focus.item4")}</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership, David Thomas */}
      <section className="py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-14">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              {/* Portrait */}
              <div className="lg:col-span-5">
                <FocalPointPortrait
                  src={davidThomasPortrait}
                  alt={t("capital.david.alt")}
                  initials="DT"
                />
                <div className="mt-6 space-y-3">
                  <p className="text-lg font-bold tracking-tight">{t("capital.david.name")}</p>
                  <p className="text-sm text-muted-foreground font-serif">
                    {t("capital.david.role")}
                  </p>
                  <div className="flex flex-wrap gap-3 pt-2">
                    <a
                      href="https://www.linkedin.com/in/davidthomas-asia/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-border bg-background text-sm font-medium tracking-wide hover:border-accent hover:text-accent transition-colors font-sans"
                    >
                      <Linkedin className="h-4 w-4" />
                      {t("capital.david.linkedin")}
                    </a>
                    <a
                      href="https://www.davidthomas.asia"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-border bg-background text-sm font-medium tracking-wide hover:border-accent hover:text-accent transition-colors font-sans"
                    >
                      <Globe className="h-4 w-4" />
                      {t("capital.david.website")}
                    </a>
                  </div>
                </div>
              </div>

              {/* Intro */}
              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                  {t("capital.david.eyebrow")}
                </p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                  {t("capital.david.title")}
                </h2>
                <p className="text-lg text-muted-foreground font-serif leading-relaxed">
                  {t("capital.david.intro")}
                </p>
                <div className="space-y-5 text-base text-muted-foreground font-serif leading-relaxed border-l-2 border-accent pl-6">
                  <p>
                    {t("capital.david.bio1")}
                  </p>
                  <p>
                    {t("capital.david.bio2")}
                  </p>
                  <p className="text-foreground font-medium">
                    {t("capital.david.bio3")}
                  </p>
                </div>
              </div>
            </div>

            {/* Credential stats */}
            <div className="grid md:grid-cols-3 gap-px bg-border/60 border border-border/60">
              <div className="bg-background p-8 space-y-2">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.david.stat1.label")}</p>
                <p className="text-lg font-bold tracking-tight">{t("capital.david.stat1.value")}</p>
                <p className="text-sm text-muted-foreground font-serif">{t("capital.david.stat1.detail")}</p>
              </div>
              <div className="bg-background p-8 space-y-2">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.david.stat2.label")}</p>
                <p className="text-lg font-bold tracking-tight">{t("capital.david.stat2.value")}</p>
                <p className="text-sm text-muted-foreground font-serif">{t("capital.david.stat2.detail")}</p>
              </div>
              <div className="bg-background p-8 space-y-2">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.david.stat3.label")}</p>
                <p className="text-lg font-bold tracking-tight">{t("capital.david.stat3.value")}</p>
                <p className="text-sm text-muted-foreground font-serif">{t("capital.david.stat3.detail")}</p>
              </div>
            </div>

            {/* Extended credentials */}
            <div className="grid md:grid-cols-2 gap-10 lg:gap-14">
              <div className="space-y-4">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.david.credentials.label")}</p>
                <h3 className="text-2xl font-bold tracking-tight">{t("capital.david.credentials.title")}</h3>
                <ul className="space-y-3 text-base text-muted-foreground font-serif leading-relaxed">
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.david.credentials.item1")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.david.credentials.item2")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.david.credentials.item3")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">·</span><span>{t("capital.david.credentials.item4")}</span></li>
                </ul>
              </div>
              <div className="space-y-4">
                <p className="text-xs font-mono text-accent tracking-widest">{t("capital.david.focus.label")}</p>
                <h3 className="text-2xl font-bold tracking-tight">{t("capital.david.focus.title")}</h3>
                <ul className="space-y-3 text-base text-muted-foreground font-serif leading-relaxed">
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.david.focus.item1")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.david.focus.item2")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.david.focus.item3")}</span></li>
                  <li className="flex gap-3"><span className="text-accent font-mono text-sm pt-1.5">→</span><span>{t("capital.david.focus.item4")}</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning */}
      <section className="py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("capital.positioning.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("capital.positioning.title")}
            </h2>
            <div className="space-y-5 text-lg text-muted-foreground font-serif leading-relaxed">
              <p>{t("capital.positioning.p1")}</p>
              <p>{t("capital.positioning.p2")}</p>
              <p>{t("capital.positioning.p3")}</p>
              <ul className="space-y-3 pl-6 list-disc marker:text-accent">
                <li>{t("capital.positioning.item1")}</li>
                <li>{t("capital.positioning.item2")}</li>
                <li>{t("capital.positioning.item3")}</li>
              </ul>
              <p className="text-foreground font-medium pt-2">
                {t("capital.positioning.closing1")}
                <br />
                {t("capital.positioning.closing2")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-28 bg-secondary/30 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="space-y-6 max-w-3xl">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                {t("capital.process.eyebrow")}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                {t("capital.process.title")}
              </h2>
              <p className="text-lg text-muted-foreground font-serif">
                {t("capital.process.intro")}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-px bg-border/60 border border-border/60">
              {process.map((step, i) => (
                <div key={step.title} className="bg-background p-8 sm:p-10 space-y-4">
                  <div className="flex items-baseline gap-4">
                    <span className="text-xs font-mono text-accent tracking-widest">
                      0{i + 1}
                    </span>
                    <h3 className="text-2xl font-bold tracking-tight">{step.title}</h3>
                  </div>
                  <p className="text-muted-foreground font-serif leading-relaxed">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* APAC Advantage */}
      <section className="py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("capital.advantage.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("capital.advantage.title")}
            </h2>
            <p className="text-lg text-muted-foreground font-serif">
              {t("capital.advantage.intro")}
            </p>
            <div className="flex flex-wrap gap-3">
              {regions.map((r) => (
                <span
                  key={r}
                  className="px-5 py-2.5 border border-border bg-background text-sm font-medium tracking-wide font-sans"
                >
                  {r}
                </span>
              ))}
            </div>
            <div className="space-y-4 text-lg text-muted-foreground font-serif leading-relaxed pt-4">
              <p>{t("capital.advantage.p1")}</p>
              <p className="text-foreground font-medium">{t("capital.advantage.p2")}</p>
              <p>{t("capital.advantage.p3")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Investor Discipline */}
      <section className="py-28 bg-secondary/30 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("capital.discipline.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("capital.discipline.title")}
            </h2>
            <p className="text-lg text-muted-foreground font-serif">
              {t("capital.discipline.p1")}
            </p>
            <p className="text-lg text-muted-foreground font-serif">
              {t("capital.discipline.p2")}
            </p>
            <div className="grid sm:grid-cols-3 gap-4 pt-2">
              {discipline.map((d) => (
                <div
                  key={d}
                  className="border border-border bg-background p-8 text-center"
                >
                  <p className="text-xl font-bold tracking-tight capitalize">{d}</p>
                </div>
              ))}
            </div>
            <p className="text-base text-muted-foreground font-serif italic pt-4">
              {t("capital.discipline.note")}
            </p>
          </div>
        </div>
      </section>

      {/* Commercial Alignment */}
      <section className="py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("capital.commercial.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("capital.commercial.title")}
            </h2>
            <p className="text-lg text-muted-foreground font-serif">
              {t("capital.commercial.p1")}
            </p>
            <p className="text-lg text-muted-foreground font-serif">{t("capital.commercial.p2")}</p>
            <ul className="space-y-4">
              {commercial.map((c) => (
                <li
                  key={c}
                  className="flex gap-4 text-lg text-foreground/90 font-serif border-b border-border/40 pb-4"
                >
                  <span className="text-accent font-mono text-sm pt-1.5">·</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <p className="text-base text-muted-foreground font-serif italic pt-2">
              {t("capital.commercial.note")}
            </p>
          </div>
        </div>
      </section>

      {/* Founder Expectation */}
      <section className="py-28 bg-secondary/30 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("capital.expectation.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("capital.expectation.title")}
            </h2>
            <p className="text-lg text-muted-foreground font-serif">
              {t("capital.expectation.p1")}
            </p>
            <p className="text-lg text-muted-foreground font-serif">{t("capital.expectation.p2")}</p>
            <ul className="space-y-4">
              {expectations.map((e) => (
                <li key={e} className="flex gap-4 text-lg text-foreground/90 font-serif">
                  <span className="text-accent font-mono text-sm pt-1.5">→</span>
                  <span>{e}</span>
                </li>
              ))}
            </ul>
            <div className="pt-4 space-y-2 text-foreground font-serif">
              <p>{t("capital.expectation.note1")}</p>
              <p className="font-medium">{t("capital.expectation.note2")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 bg-gradient-to-br from-primary via-foreground/95 to-foreground/90 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("capital.cta.title")}
            </h2>
            <div className="space-y-4 text-lg text-white/75 font-serif leading-relaxed max-w-2xl mx-auto">
              <p>{t("capital.cta.p1")}</p>
              <p>{t("capital.cta.p2")}</p>
            </div>
            <Button variant="hero" size="xl" asChild className="group font-sans">
              <Link to="/founder-readiness">
                {t("capital.cta.button")}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <p className="text-sm text-white/50 font-serif italic pt-8 leading-relaxed">
              {t("capital.cta.tagline1")}
              <br />
              {t("capital.cta.tagline2")}
              <br />
              {t("capital.cta.tagline3")}
            </p>
            <div className="pt-4">
              <button
                onClick={() => window.open(BOOKING_URL, "_blank")}
                className="text-sm font-medium text-white/70 hover:text-white link-underline font-sans"
              >
                {t("capital.cta.book")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default CapitalRaising;
